// GIC&E contractor training lessons shown in the unlocked training portal.
// Module ids MUST stay in sync with TRAINING_MODULE_IDS in backend/server.py.

export const TRAINING_MODULES = [
  {
    id: "core_values",
    title: "Living the GIC&E Core Values",
    minutes: 6,
    intro: "Everything at GIC&E begins with how we treat people. Before the clinical work, we ground ourselves in who we are.",
    sections: [
      { heading: "Heart-centered, and accountable", paragraphs: [
        "Heart-centered leadership does not mean the absence of accountability. We hold ourselves and each other to high standards while honoring the humanity of every client, family, and coworker.",
        "You will be trusted with people at vulnerable moments. Lead with empathy — and follow through on what you commit to.",
      ] },
      { heading: "Empowerment over rescue", paragraphs: [
        "We don't enter people's lives to save them. We help them recognize and strengthen their own capacity, and we collaborate with families, schools, and treatment teams because change rarely happens in isolation.",
      ], bullets: [
        "Partner with clients, don't do the work for them.",
        "Celebrate small wins — progress compounds.",
        "Stay curious and coachable; growth is expected here.",
      ] },
    ],
  },
  {
    id: "scope_roles",
    title: "Role, Scope & Working Within Boundaries",
    minutes: 7,
    intro: "Knowing what is — and is not — yours to handle protects clients and keeps services compliant.",
    sections: [
      { heading: "Stay within your assigned scope", paragraphs: [
        "Every role has a defined scope. Providing a service outside your role, or attempting to handle a need that requires another qualified professional, puts the client at risk and the service out of compliance.",
      ], bullets: [
        "Recognize needs that require care coordination or resource linkage.",
        "Refer clinical decisions (diagnosis, medication, safety planning) to the appropriate professional.",
        "Communicate promptly with the treatment team when something falls outside your lane.",
      ] },
      { heading: "When in doubt, coordinate", paragraphs: [
        "It is always appropriate to escalate. A quick message to the treatment team is far better than guessing. Boundaries are not barriers — they are how a team keeps clients safe.",
      ] },
    ],
  },
  {
    id: "documentation",
    title: "Documentation That Reflects the Service",
    minutes: 8,
    intro: "A strong note lets any reader understand why the service was needed, what you did, how the client responded, and what happens next.",
    sections: [
      { heading: "Why / What / Response / Next", paragraphs: [
        "Connect each encounter to the client's assessed need and treatment-plan objective. Then describe the observable intervention you actually delivered.",
      ], bullets: [
        "WHY: tie the service to a treatment-plan objective and medical necessity.",
        "WHAT: what you taught, modeled, practiced, developed, coordinated, or assessed.",
        "RESPONSE: what the client said, demonstrated, practiced, or struggled with.",
        "NEXT: the next step, connected to the treatment plan.",
      ] },
      { heading: "Avoid vague documentation", paragraphs: [
        'Notes like "Talked with client," "Discussed coping," or "Worked on goals" do not describe a billable, meaningful service.',
        "Do not copy the treatment plan into your note. Document what actually occurred during the encounter.",
      ] },
    ],
  },
  {
    id: "billing_compliance",
    title: "Billing & Compliance Fundamentals",
    minutes: 6,
    intro: "Time spent with a client does not automatically make an activity billable. Documentation and requirements matter.",
    sections: [
      { heading: "What makes a service billable", paragraphs: [
        "Billable services are supported by appropriate documentation, medical necessity, treatment-plan alignment, and applicable payer and program requirements.",
      ], bullets: [
        "Never document or bill for a service you did not provide.",
        "Documentation must accurately reflect the service actually performed.",
        "Duplicate, unsupported, falsified, or altered documentation may result in nonpayment and/or termination.",
        "Compensation is governed by your executed contractor agreement.",
      ] },
    ],
  },
  {
    id: "treatment_plan",
    title: "From Treatment Plan to Real Encounter",
    minutes: 7,
    intro: "Your job is to translate an individualized treatment plan into a meaningful, in-person (or telehealth) service.",
    sections: [
      { heading: "Pick a goal, deliver a skill, capture the response", paragraphs: [
        "Select the goal/objective you will address, choose a concrete skill or intervention, and give the client an active way to practice it — not just discuss it.",
        "If the client refuses to participate, meet them where they are, adjust your approach, and document the barrier and your response.",
      ], bullets: [
        "Turn objectives into concrete, practiced activities.",
        "Note barriers (e.g., no school placement) and coordinate appropriately.",
        "End every encounter with a treatment-plan-connected next step.",
      ] },
    ],
  },
];
